import { CombinedTask } from "@/_models/combinedTask.interface";
import { Injectable } from "@angular/core";
import {
    BehaviorSubject,
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    map,
    Observable,
    shareReplay,
    startWith,
    switchMap,
    tap,
} from "rxjs";
import { SortingHomeService } from "./sorting-home.service";
import { HomeFilterService } from "./home-filter.service";
import { FlightService } from "./flight.service";

@Injectable({
    providedIn: "root",
})
export class CombinedTasksService {
    reload$ = new BehaviorSubject<void>(undefined);
    private page$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    private loading$ = new BehaviorSubject<boolean>(false);
    pageSize: number = 10;
    total: number = 0;

    get isLoading$(): Observable<boolean> {
        return this.loading$.asObservable();
    }

    get currentPage(): number {
        return this.page$.value;
    }

    constructor(
        private sortingService: SortingHomeService,
        private homeFilterService: HomeFilterService,
        private flightService: FlightService,
    ) {}

    setPage(page: number) {
        this.page$.next(page);
    }

    loadCombinedTasks(): Observable<CombinedTask[]> {
        return this.reload$.pipe(
            tap(() => this.loading$.next(true)),
            switchMap(() =>
                combineLatest([
                    this.page$,
                    this.sortingService.currentSorting$,
                    this.homeFilterService.form.valueChanges.pipe(
                        startWith(this.homeFilterService.form.value),
                        debounceTime(300),
                    ),
                ]),
            ),
            distinctUntilChanged(
                (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
            ),
            switchMap(([page, sorting, formValues]) => {
                const filters: Record<string, any> = {
                    assignedToMe: "true",
                };

                const { startDate, endDate } = formValues.dateRange || {};

                // Handle both Date objects and ISO strings
                if (startDate) {
                    filters.startDate =
                        startDate instanceof Date
                            ? startDate.toISOString()
                            : startDate;
                }
                if (endDate) {
                    filters.endDate =
                        endDate instanceof Date
                            ? endDate.toISOString()
                            : endDate;
                }

                if (formValues.formType) filters.type = formValues.formType;
                if (formValues.pastDue) filters.pastDue = "true";

                return this.flightService.getCombinedTasks(
                    formValues.sortOrder || sorting.value,
                    page,
                    this.pageSize,
                    filters,
                );
            }),
            tap((list) => {
                this.total = list.meta.total;
                this.loading$.next(false);
            }),
            map((list) => list.data),
            shareReplay(1),
        );
    }
}
